import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Share2, Lock, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
interface BookingConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  businessName: string;
  serviceName: string;
  isSubmitting?: boolean;
}
export function BookingConsentModal({
  open,
  onOpenChange,
  onConfirm,
  businessName,
  serviceName,
  isSubmitting = false
}: BookingConsentModalProps) {
  const [sharingAgreed, setSharingAgreed] = useState(false);
  const handleConfirm = () => {
    if (sharingAgreed) {
      onConfirm();
    }
  };
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSharingAgreed(false);
    }
    onOpenChange(newOpen);
  };
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle className="font-display text-xl">
              Consent to Share Information
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-secondary/50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Share2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                To complete your booking for <span className="font-medium text-foreground">{serviceName}</span> at{" "}
                <span className="font-medium text-foreground">{businessName}</span>, your relevant personal 
                information will be shared with the selected service provider for service delivery, 
                communication, and record-keeping purposes.
              </p>
            </div>
            
            <div className="flex items-start gap-3">
              <CreditCard className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">Payments are processed directly at the wellness center and are not stored by Wellness Connect.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border bg-background">
            <Checkbox id="sharing-consent" checked={sharingAgreed} onCheckedChange={checked => setSharingAgreed(checked === true)} />
            <Label htmlFor="sharing-consent" className="text-sm leading-relaxed cursor-pointer">
              I consent to the sharing of my information for the purpose of completing 
              this booking, in accordance with the{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>.
            </Label>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="w-3 h-3" />
            <span>Your information is handled in compliance with applicable privacy laws</span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="hero" onClick={handleConfirm} disabled={!sharingAgreed || isSubmitting}>
            {isSubmitting ? "Confirming..." : "Confirm Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}